import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2, ShieldCheck, CheckCircle2, XCircle, Clock, ExternalLink, ArrowRight,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users, User,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { apiClient, type WorkflowApproval } from '../api/client';

import { UserAvatar } from '../components/UserAvatar';

import slackColorIcon from '../assets/icons/slack-color.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';

const WorkflowApprovals: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [approvals, setApprovals] = useState<WorkflowApproval[]>([]);
  const [resolvedApprovals, setResolvedApprovals] = useState<WorkflowApproval[]>([]);
  const [resolvedTotal, setResolvedTotal] = useState(0);
  const [resolvedTotalPages, setResolvedTotalPages] = useState(0);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const PENDING_PER_PAGE = 15;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userMap, setUserMap] = useState<Record<string, { picture_url?: string; given_name?: string; family_name?: string }>>({});
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [groupMap, setGroupMap] = useState<Record<string, string>>({});
  const [userIdMap, setUserIdMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!authLoading && user) {
      loadApprovals(true);
      const interval = setInterval(() => loadApprovals(false), 15000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

    const loadApprovals = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [pending, resolvedData] = await Promise.all([
        apiClient.listPendingApprovals(),
        apiClient.listResolvedApprovals(1, 10),
      ]);
      setApprovals(pending);
      setResolvedApprovals(resolvedData.approvals || []);
      setResolvedTotal(resolvedData.total || 0);
      setResolvedTotalPages(resolvedData.total_pages || 0);

      try {
        const usersData = await apiClient.listTenantUsers();
        const users = usersData.users || usersData || [];
        const map: Record<string, { picture_url?: string; given_name?: string; family_name?: string }> = {};
        const idMap: Record<string, string> = {};
        for (const u of users) {
          if (u.email) map[u.email] = { picture_url: u.picture_url, given_name: u.given_name, family_name: u.family_name };
          if (u.id) idMap[u.id] = u.email || u.name || u.id;
        }
        setUserMap(map);
        setUserIdMap(idMap);
      } catch { /* non-fatal */ }

      try {
        const groups = await apiClient.listUserGroups();
        const gMap: Record<string, string> = {};
        for (const g of (groups || [])) {
          if (g.id) gMap[g.id] = g.name || g.id;
        }
        setGroupMap(gMap);
      } catch { /* non-fatal */ }
    } catch (err: any) {
      setError(err?.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    setProcessingIds(prev => new Set(prev).add(id));
    try {
      await apiClient.approveWorkflowStep(id);
      const approved = approvals.find(a => a.id === id);
      setApprovals(prev => {
        const updated = prev.filter(a => a.id !== id);
        if (updated.length <= (pendingPage - 1) * PENDING_PER_PAGE && pendingPage > 1) setPendingPage(1);
        return updated;
      });
      if (approved) {
        setResolvedApprovals(prev => [{ ...approved, status: 'approved' as const, responded_at: new Date().toISOString() }, ...prev]);
      }
      setSuccessMessage('Approval granted — workflow execution will resume.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Failed to approve');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleReject = async (id: string) => {
    setProcessingIds(prev => new Set(prev).add(id));
    try {
      await apiClient.rejectWorkflowStep(id);
      const rejected = approvals.find(a => a.id === id);
      setApprovals(prev => {
        const updated = prev.filter(a => a.id !== id);
        if (updated.length <= (pendingPage - 1) * PENDING_PER_PAGE && pendingPage > 1) setPendingPage(1);
        return updated;
      });
      if (rejected) {
        setResolvedApprovals(prev => [{ ...rejected, status: 'rejected' as const, responded_at: new Date().toISOString() }, ...prev]);
      }
      setSuccessMessage('Approval rejected — workflow execution will be stopped.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Failed to reject');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {successMessage && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {approvals.length === 0 && resolvedApprovals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-semibold">No pending approvals</h3>
            <p className="text-sm text-muted-foreground mt-1">
              When a workflow step requires approval, it will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {approvals.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold">Pending ({approvals.length})</h2>
              <div className="rounded-lg border overflow-x-auto md:overflow-visible">
                <table className="w-full text-sm min-w-[720px] md:min-w-0">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-10"></th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Workflow</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Context</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Approvers</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Requested</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Execution</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvals.slice((pendingPage - 1) * PENDING_PER_PAGE, pendingPage * PENDING_PER_PAGE).map(approval => {
                      const isDark = document.documentElement.classList.contains('dark');
                      const ctx = approval.context || {};
                      const workflowName = ctx.workflow_name || 'Workflow';
                      const workflowId = ctx.workflow_id || '';
                      const incident = ctx.incident || {};
                      const contextText = ctx.justification_text || ctx.prompt_message || incident.title || '';
                      const timeAgo = getTimeAgo(new Date(approval.requested_at));
                      const isProc = processingIds.has(approval.id);
                      const executionLink = workflowId
                        ? `/workflows/${workflowId}/executions/${approval.execution_id}`
                        : null;

                      const approverLabels = getApproverLabels(approval, groupMap, userIdMap);

                      return (
                        <PendingApprovalRow
                          key={approval.id}
                          approval={approval}
                          isDark={isDark}
                          workflowName={workflowName}
                          executionLink={executionLink}
                          contextText={contextText}
                          approverLabels={approverLabels}
                          timeAgo={timeAgo}
                          isProcessing={isProc}
                          onApprove={handleApprove}
                          onReject={handleReject}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {approvals.length > PENDING_PER_PAGE && (() => {
                const totalPages = Math.ceil(approvals.length / PENDING_PER_PAGE);
                return (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-muted-foreground">Showing {(pendingPage - 1) * PENDING_PER_PAGE + 1}–{Math.min(pendingPage * PENDING_PER_PAGE, approvals.length)} of {approvals.length}</span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPendingPage(p => Math.max(1, p - 1))} disabled={pendingPage === 1}>Previous</Button>
                      <span className="text-sm text-muted-foreground">Page {pendingPage} of {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setPendingPage(p => Math.min(totalPages, p + 1))} disabled={pendingPage >= totalPages}>Next</Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {approvals.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-sm text-muted-foreground">No pending approvals</p>
              </CardContent>
            </Card>
          )}

          {resolvedApprovals.length > 0 && (
            <ResolvedApprovalsTable
              approvals={resolvedApprovals}
              total={resolvedTotal}
              totalPages={resolvedTotalPages}
              page={resolvedPage}
              userMap={userMap}
              onPageChange={async (p) => {
                setResolvedPage(p);
                try {
                  const data = await apiClient.listResolvedApprovals(p, 10);
                  setResolvedApprovals(data.approvals || []);
                  setResolvedTotal(data.total || 0);
                  setResolvedTotalPages(data.total_pages || 0);
                } catch { /* ignore */ }
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

function useIsDark() {
  const { theme } = useTheme();
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

function getApprovalIcon(approval: WorkflowApproval, isDark: boolean): React.ReactNode {
  const { approval_type, pr_url } = approval;
  if (approval_type === 'pr_approval' || approval_type === 'pr_merge') {
    if (pr_url?.includes('gitlab')) {
      return <img src={gitlabIcon} alt="GitLab" className="w-5 h-5" />;
    }
    return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-5 h-5" />;
  }
  if (approval_type === 'slack') {
    return <img src={slackColorIcon} alt="Slack" className="w-5 h-5" />;
  }
  return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className="w-5 h-5" />;
}

function getApprovalTypeLabel(approvalType: string): string {
  switch (approvalType) {
    case 'pr_approval': return 'PR Review';
    case 'pr_merge': return 'PR Merge';
    case 'slack': return 'Slack';
    case 'manual': return 'Manual';
    case 'justification': return 'Justification';
    default: return approvalType;
  }
}

function ApprovalCard({ approval, isProcessing, onApprove, onReject }: {
  approval: WorkflowApproval;
  isProcessing: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const isDark = useIsDark();
  const ctx = approval.context || {};
  const workflowName = ctx.workflow_name || 'Workflow';
  const workflowId = ctx.workflow_id || '';
  const incident = ctx.incident || {};
  const timeAgo = getTimeAgo(new Date(approval.requested_at));

  const executionLink = workflowId
    ? `/workflows/${workflowId}/executions/${approval.execution_id}`
    : null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 shrink-0 mt-0.5">
              {getApprovalIcon(approval, isDark)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">{workflowName}</h3>
                <Badge variant="outline" className="text-xs">{getApprovalTypeLabel(approval.approval_type)}</Badge>
                <Badge variant="secondary" className="text-xs">Step: {approval.step_id}</Badge>
              </div>

              {incident.title && (
                <p className="text-sm text-muted-foreground mt-0.5 truncate">
                  {incident.title}
                </p>
              )}

              {incident.namespace && incident.resource_name && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {incident.resource_kind || 'Pod'}: {incident.namespace}/{incident.resource_name}
                </p>
              )}

              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {timeAgo}
                </span>
              </div>

              {/* External source link + execution link */}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {approval.pr_url && (
                  <a
                    href={approval.pr_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {getApprovalIcon(approval, isDark)}
                    <span>{approval.pr_url.includes('gitlab') ? 'View MR' : 'View PR'}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                )}
                {executionLink && (
                  <Link
                    to={executionLink}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ArrowRight className="w-3 h-3" />
                    <span>View Execution</span>
                  </Link>
                )}
              </div>

              <ApprovalProgressIndicator approval={approval} />
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {(approval.approval_type === 'pr_approval' || approval.approval_type === 'pr_merge') ? (
              approval.pr_url ? (
                <a
                  href={approval.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  {approval.pr_url.includes('gitlab') ? 'Review MR' : 'Review PR'}
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">Waiting for PR event...</span>
              )
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReject(approval.id)}
                  disabled={isProcessing}
                  className="gap-1 text-destructive hover:text-destructive"
                >
                  {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => onApprove(approval.id)}
                  disabled={isProcessing}
                  className="gap-1"
                >
                  {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ExpandableCell({ text, maxWidth = 'max-w-[250px]' }: { text: string; maxWidth?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  if (!text) return <span className="text-muted-foreground">—</span>;
  return (
    <div className={`${maxWidth} relative`}>
      <div className="flex items-center gap-1">
        <span ref={textRef} className="truncate">{text}</span>
        {isTruncated && (
          <button onClick={() => setExpanded(!expanded)} className="shrink-0 text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>
      {expanded && isTruncated && (
        <div className="absolute z-50 left-0 top-full mt-1 p-2 rounded-md border bg-popover text-popover-foreground shadow-md text-xs max-w-[400px] whitespace-normal break-words">
          {text}
        </div>
      )}
    </div>
  );
}

function getApproverLabels(approval: WorkflowApproval, groupMap?: Record<string, string>, userIdMap?: Record<string, string>): string[] {
  const rules = approval.approval_rules;
  if (!rules || rules.length === 0) return [];
  const labels: string[] = [];
  for (const rule of rules) {
    for (const entry of rule.entries) {
      if (entry.label) {
        labels.push(entry.label);
      } else if (entry.type === 'group' && groupMap?.[entry.id]) {
        labels.push(groupMap[entry.id]);
      } else if (entry.type === 'user' && userIdMap?.[entry.id]) {
        labels.push(userIdMap[entry.id]);
      } else {
        labels.push(entry.id.slice(0, 8));
      }
    }
  }
  return labels;
}

function PendingApprovalRow({ approval, isDark, workflowName, executionLink, contextText, approverLabels, timeAgo, isProcessing, onApprove, onReject }: {
  approval: WorkflowApproval;
  isDark: boolean;
  workflowName: string;
  executionLink: string | null;
  contextText: string;
  approverLabels: string[];
  timeAgo: string;
  isProcessing: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  // Refine-approval gates carry a refine_round counter in their context. The
  // free-text feedback + "Request changes" flow lives on the execution detail
  // page (where the reviewer can see the RCA output it applies to), so here we
  // only surface a "Review & respond" link.
  const ctx = (approval.context || {}) as Record<string, any>;
  const isRefine = ctx.refine_round !== undefined || ctx.max_rounds !== undefined;
  return (
    <tr className="border-b last:border-0 hover:bg-muted/20 transition-colors">
      <td className="px-4 py-2.5">
        <div className="flex items-center justify-center w-7 h-7">
          {getApprovalIcon(approval, isDark)}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium">{workflowName}</div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1 flex-wrap">
          <Badge variant="outline" className="text-xs">{getApprovalTypeLabel(approval.approval_type)}</Badge>
        </div>
      </td>
      <td className="px-4 py-2.5 text-xs">
        <ExpandableCell text={contextText} maxWidth="max-w-[250px]" />
      </td>
      <td className="px-4 py-2.5">
        {approverLabels.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {approverLabels.map((label, i) => (
              <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">{label}</Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Anyone</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-muted-foreground text-xs">{timeAgo}</td>
      <td className="px-4 py-2.5">
        {executionLink && (
          <Link to={executionLink} className="text-xs text-primary hover:underline">View Execution</Link>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="flex items-center gap-2 justify-end">
          {(approval.approval_type === 'pr_approval' || approval.approval_type === 'pr_merge') ? (
            approval.pr_url ? (
              <a href={approval.pr_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors">
                <ExternalLink className="w-3 h-3" />
                {approval.pr_url.includes('gitlab') ? 'Review MR' : 'Review PR'}
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">Waiting...</span>
            )
          ) : isRefine ? (
            // Refine gates require feedback context; resolve them from the
            // execution detail sidebar instead of the dense table.
            executionLink ? (
              <Link to={executionLink} className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors">
                <ArrowRight className="w-3 h-3" /> Review &amp; respond
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">Open execution to respond</span>
            )
          ) : (
            <>
              <Button size="sm" onClick={() => onApprove(approval.id)} disabled={isProcessing} className="gap-1 h-7 text-xs">
                {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
              </Button>
              <Button variant="outline" size="sm" onClick={() => onReject(approval.id)} disabled={isProcessing} className="h-7 text-xs">
                Reject
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function ResolvedApprovalsTable({ approvals, total, totalPages, page, onPageChange, userMap }: {
  approvals: WorkflowApproval[];
  total: number;
  totalPages: number;
  page: number;
  onPageChange: (page: number) => void;
  userMap?: Record<string, { picture_url?: string; given_name?: string; family_name?: string }>;
}) {
  const isDark = useIsDark();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="space-y-3">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        Recent Approvals ({total})
      </button>
      {!collapsed && (
        <>
      <div className="rounded-lg border overflow-x-auto md:overflow-visible">
        <table className="w-full text-sm min-w-[720px] md:min-w-0">
          <thead>
            <tr className="border-b">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-10"></th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Workflow</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">By</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Resolved</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Execution</th>
            </tr>
          </thead>
          <tbody>
            {approvals.map(approval => {
              const ctx = approval.context || {};
              const workflowName = ctx.workflow_name || 'Workflow';
              const workflowId = ctx.workflow_id || '';
              const respondedAt = approval.responded_at
                ? new Date(approval.responded_at).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) + ' UTC'
                : '—';
              const executionLink = workflowId
                ? `/workflows/${workflowId}/executions/${approval.execution_id}`
                : null;
              const statusColor = approval.status === 'approved'
                ? 'text-green-600 dark:text-green-400'
                : approval.status === 'rejected'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-muted-foreground';
              const statusLabel = approval.status === 'approved' ? 'Approved' : approval.status === 'rejected' ? 'Rejected' : 'Expired';

              return (
                <tr
                  key={approval.id}
                  className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${executionLink ? 'cursor-pointer' : ''}`}
                  onClick={() => { if (executionLink) window.location.href = executionLink; }}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-center w-7 h-7">
                      {getApprovalIcon(approval, isDark)}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-medium">{workflowName}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className="text-xs">{getApprovalTypeLabel(approval.approval_type)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">
                    {approval.responded_by_email ? (
                      <div className="flex items-center gap-1.5">
                        <UserAvatar
                          email={approval.responded_by_email}
                          pictureUrl={userMap?.[approval.responded_by_email]?.picture_url}
                          givenName={userMap?.[approval.responded_by_email]?.given_name}
                          familyName={userMap?.[approval.responded_by_email]?.family_name}
                        />
                        <span className="truncate">{approval.responded_by_email}</span>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{respondedAt}</td>
                  <td className="px-4 py-2.5">
                    {executionLink && (
                      <Link to={executionLink} className="text-xs text-primary hover:underline">View Execution</Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm text-muted-foreground">
            Showing {(page - 1) * 10 + 1}–{Math.min(page * 10, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

function ApprovalProgressIndicator({ approval }: { approval: WorkflowApproval }) {
  const rules = approval.approval_rules;
  const responses = approval.approval_responses || [];
  if (!rules || rules.length === 0) return null;

  const approvedUserIds = new Set(
    responses.filter(r => r.status === 'approved').map(r => r.user_id)
  );

  return (
    <div className="mt-2 space-y-1.5">
      {rules.map((rule, ruleIdx) => (
        <div key={ruleIdx}>
          {ruleIdx > 0 && <div className="text-[10px] text-muted-foreground uppercase tracking-wider text-center my-1">or</div>}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-muted-foreground mr-0.5">Needs:</span>
            {rule.entries.map((entry, i) => {
              const satisfied = entry.type === 'user'
                ? approvedUserIds.has(entry.id)
                : false;
              return (
                <Badge
                  key={`${entry.type}-${entry.id}`}
                  variant={satisfied ? 'default' : 'outline'}
                  className={`text-[10px] px-1.5 py-0 gap-1 ${satisfied ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700' : ''}`}
                >
                  {entry.type === 'group' ? <Users className="w-2.5 h-2.5" /> : <User className="w-2.5 h-2.5" />}
                  {entry.label || entry.id.slice(0, 8)}
                  {satisfied && <CheckCircle2 className="w-2.5 h-2.5" />}
                  {i < rule.entries.length - 1 && <span className="text-muted-foreground ml-0.5">&</span>}
                </Badge>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function getTimeAgo(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default WorkflowApprovals;
